/**
 * The running job's abort signal, carried implicitly to code that cannot be handed it.
 *
 * WHY NOT A PARAMETER. The consumer is the model gate, and it is reached through the agent modules — sixteen files
 * whose functions have no reason to know a job exists. Threading `signal?: AbortSignal` through all of them and their
 * pipeline callers would touch every one to pass `undefined` today, and would still be the wrong shape: a router is
 * frequently built once and reused (`default-grading.ts` memoises one at module scope), so a signal attached at
 * CONSTRUCTION belongs to the wrong lifetime. One job's cancellation would abort another job's calls, silently and
 * only under concurrency — the worst kind of bug to introduce for the sake of tidiness.
 *
 * The signal belongs to the CALL, not to the router, and async context is the only thing in Node that expresses that
 * without a parameter. `execute` opens a scope around one handler invocation; anything that runs inside it — however
 * deep, across any number of `await`s — reads the same signal, and anything outside reads none.
 *
 * The cost is that it is implicit, so: it is READ IN EXACTLY ONE PLACE (`acquire` in `llm/llm-gate.ts`), and absence
 * is always safe. A caller with no scope simply gets no cancellation, which is what every non-job caller — chat, a
 * script, a test — should get.
 *
 * KNOWN LIMIT: async context does not cross into `worker_threads`. No model call is made from one, so this does not
 * arise today; a worker that needed cancellation would have to be passed a signal explicitly.
 */

import { AsyncLocalStorage } from 'node:async_hooks';

/**
 * CONSTRUCTED ON FIRST USE, not at module scope, and that is a browser requirement rather than a preference.
 *
 * This module is reached from the client bundle: `db/index.ts` imports the agent modules to register their entities
 * and mutations, an agent module imports the model gate, and the gate reads the ambient signal. The bundler shims
 * `node:async_hooks` to `undefined` for the browser, so a module-scope `new AsyncLocalStorage()` becomes
 * `new (void 0)()` — a TypeError thrown while the page's first script is still initialising, which takes the whole
 * client down. Nothing in the browser ever opens a scope, so nothing there ever constructs it.
 */
let jobSignals: AsyncLocalStorage<AbortSignal> | undefined = undefined;

/**
 * Runs `fn` with `signal` as the ambient job signal. Returns whatever `fn` returns, so an async `fn`'s whole promise
 * chain inherits the scope — that is the property the whole mechanism rests on.
 */
export function runWithJobSignal<T>(signal: AbortSignal, fn: () => T): T {
  jobSignals ??= new AsyncLocalStorage<AbortSignal>();
  return jobSignals.run(signal, fn);
}

/**
 * The signal of the job on whose behalf this code is running, or `undefined` outside a job.
 *
 * Reads through the optional chain rather than seeding the store: no scope has ever been opened means there is no
 * job, which is exactly the answer — and it keeps the READ side from constructing anything in the browser.
 */
export function currentJobSignal(): AbortSignal | undefined {
  return jobSignals?.getStore();
}
